import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import logger from '../utils/logger';
interface CelebrationEffectProps {
    trigger: boolean;
    onComplete?: () => void;
    savedForLater?: boolean;
}

export default function CelebrationEffect({
    trigger,
    onComplete,
    savedForLater = false,
}: CelebrationEffectProps) {
    useEffect(() => {
        if (trigger) {
            logger.debug(
                '[PAIR-APP] CelebrationEffect: Triggering celebration!'
            );

            // Premium celebration colors
            const celebrationColors = [
                '#a855f7', // purple-500
                '#c084fc', // purple-400
                '#fbbf24', // amber-400
                '#f59e0b', // amber-500
                '#ffffff', // white
                '#e879f9', // fuchsia-400
                '#d946ef', // fuchsia-500
            ];

            const randomInRange = (min: number, max: number) => {
                return Math.random() * (max - min) + min;
            };

            // Initial burst - fireworks style
            const fireworkBurst = () => {
                confetti({
                    particleCount: 80,
                    spread: 100,
                    origin: { x: 0.5, y: 0.5 },
                    colors: celebrationColors,
                    startVelocity: 45,
                    gravity: 1.2,
                    ticks: 200,
                    zIndex: 40,
                });
            };

            // Side cannons
            const sideCannons = () => {
                // Left cannon
                confetti({
                    particleCount: 40,
                    angle: 60,
                    spread: 50,
                    origin: { x: 0, y: 0.7 },
                    colors: celebrationColors,
                    startVelocity: 35,
                    gravity: 0.9,
                    ticks: 150,
                    zIndex: 40,
                });
                // Right cannon
                confetti({
                    particleCount: 40,
                    angle: 120,
                    spread: 50,
                    origin: { x: 1, y: 0.7 },
                    colors: celebrationColors,
                    startVelocity: 35,
                    gravity: 0.9,
                    ticks: 150,
                    zIndex: 40,
                });
            };

            // Trigger initial bursts
            fireworkBurst();
            setTimeout(sideCannons, 200);
            setTimeout(fireworkBurst, 500);

            // Gentle continuous confetti for 4 seconds
            const duration = 4000;
            const animationEnd = Date.now() + duration;

            const interval = setInterval(() => {
                const timeLeft = animationEnd - Date.now();

                if (timeLeft <= 0) {
                    clearInterval(interval);
                    // Final burst
                    confetti({
                        particleCount: 50,
                        spread: 120,
                        origin: { y: 0.6 },
                        colors: celebrationColors,
                        startVelocity: 25,
                        zIndex: 40,
                    });
                    onComplete?.();
                    return;
                }

                // Gentle falling confetti from top
                confetti({
                    particleCount: Math.floor(8 * (timeLeft / duration)),
                    startVelocity: 15,
                    spread: 100,
                    origin: {
                        x: randomInRange(0.2, 0.8),
                        y: -0.1,
                    },
                    colors: celebrationColors,
                    gravity: 0.6,
                    ticks: 200,
                    drift: randomInRange(-0.5, 0.5),
                    zIndex: 40,
                });
            }, 400);

            return () => clearInterval(interval);
        }
    }, [trigger, onComplete]);

    return (
        <AnimatePresence>
            {trigger && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="fixed inset-0 pointer-events-none z-50 flex flex-col items-center justify-center gap-4"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="text-center"
                    ></motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
